import { Stack, Text } from '@mantine/core';
import type { TFunction } from 'i18next';
import { DefaultValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Abstract } from '@/module/Author/Abstract';
import { AbstractCard } from '@/module/Author/Abstract/components/Card/AbstractCard';
import { Article } from '@/module/Author/Article';
import { ArticleBestQ } from '@/module/Author/Article/components/ArticleBestQ';
import { ArticleCard } from '@/module/Author/Article/components/Card/ArticleCard';
import { Award } from '@/module/Author/Award';
import { AwardCard } from '@/module/Author/Award/components/Card/AwardCard';
import { Book } from '@/module/Author/Book';
import { BookCard } from '@/module/Author/Book/components/Card/BookCard';
import { Chapter } from '@/module/Author/Chapter';
import { ChapterCard } from '@/module/Author/Chapter/components/Card/ChapterCard';
import { Committee } from '@/module/Author/Committee';
import { CommitteeCard } from '@/module/Author/Committee/components/Card/CommitteeCard';
import { Course } from '@/module/Author/Course';
import { CourseCard } from '@/module/Author/Course/components/Card/CourseCard';
import { Dictionary } from '@/module/Author/Dictionary';
import { DictionaryCard } from '@/module/Author/Dictionary/components/Card/DictionaryCard';
import { DoctoralCommittee } from '@/module/Author/DoctoralCommittee';
import { DoctoralCommitteeCard } from '@/module/Author/DoctoralCommittee/components/Card/DoctoralCommitteeCard';
import { EditorialTeam } from '@/module/Author/EditorialTeam';
import { EditorialTeamCard } from '@/module/Author/EditorialTeam/components/Card/EditorialTeamCard';
import { Patent } from '@/module/Author/Patent';
import { PatentCard } from '@/module/Author/Patent/components/Card/PatentCard';
import { Presentation } from '@/module/Author/Presentation';
import { PresentationCard } from '@/module/Author/Presentation/components/Card/PresentationCard';
import { Proceeding } from '@/module/Author/Proceeding';
import { ProceedingCard } from '@/module/Author/Proceeding/components/Card/ProceedingCard';
import { Project } from '@/module/Author/Project';
import { ProjectCard } from '@/module/Author/Project/components/Card/ProjectCard';
import { Translation } from '@/module/Author/Translation';
import { TranslationCard } from '@/module/Author/Translation/components/Card/TranslationCard';
import { BaseResearchPerfEvalCriteriaItemForm } from '@/module/ResearchPerfEval/form/BaseResearchPerfEvalCriteriaItemForm';
import { BaseResearchPerfEvalSelectControl } from '@/module/ResearchPerfEval/form/BaseResearchPerfEvalSelectControl';
import { EvalTemplateCriteriaType } from '@/module/ResearchPerfEval/model/EvalTemplateTypes';
import {
  BaseCriteriaItemSchema,
  abstractCriteriaItemSchema,
  articleCriteriaItemSchema,
  awardCriteriaItemSchema,
  bookCriteriaItemSchema,
  booleanCriteriaItemSchema,
  chapterCriteriaItemSchema,
  committeeCriteriaItemSchema,
  courseCriteriaItemSchema,
  dictionaryCriteriaItemSchema,
  doctoralCommitteeCriteriaItemSchema,
  editorialTeamCriteriaItemSchema,
  freeCriteriaItemSchema,
  numberCriteriaItemSchema,
  patentCriteriaItemSchema,
  presentationCriteriaItemSchema,
  proceedingCriteriaItemSchema,
  projectCriteriaItemSchema,
  translationCriteriaItemSchema,
} from '@/module/ResearchPerfEval/model/ResearchPerfEvalSchema';

import { NumberInputControl, TextInputControl } from '@/core/components';

import { Id, assertUnreachable } from '@feature/utils';

interface BaseResearchPerfEvalCriteriaItemFormProps<T extends BaseCriteriaItemSchema> {
  onSubmit: (request: BaseCriteriaItemSchema) => void;
  onCancel: () => void;

  defaultValues: DefaultValues<T>;

  isSubmitting: boolean;
  criteriaType: EvalTemplateCriteriaType;

  dataEntryHint: string | null;

  evaluationId: Id;
  authorId: Id;
  orderNo: number;

  alreadySelectedIds: number[];
}

function renderExtraFormFields(
  isSubmitting: boolean,
  criteriaType: EvalTemplateCriteriaType,
  t: TFunction,
  dataEntryHint: string | null,
  authorId: Id,
  evaluationId: Id,
  orderNo: number,
  alreadySelectedIds: number[],
) {
  if (criteriaType === 'FREE_ANSWER') {
    return (
      <TextInputControl
        withAsterisk
        name="activityDescription"
        label={t('researchPerfEval.form.activityDescription')}
        isSubmitting={isSubmitting}
      />
    );
  }

  if (criteriaType === 'BOOLEAN') {
    return (
      <Text
        fw="500"
        c="dimmed"
      >
        {dataEntryHint}
      </Text>
    );
  }

  if (criteriaType === 'NUMBER') {
    return (
      <NumberInputControl
        withAsterisk
        name="number"
        label={dataEntryHint ?? t('ResearchPerfEval.form.number')}
        isSubmitting={isSubmitting}
        allowDecimal={true}
        min={0}
      />
    );
  }

  if (criteriaType === 'SELECT_COURSE') {
    return (
      <BaseResearchPerfEvalSelectControl<Course>
        label="Curs"
        renderItem={course => <CourseCard course={course} />}
        getTitle={course => course.title}
        getId={course => course.id}
        onView={course => {
          window.open(`/author/course/${course.id}`);
        }}
        authorId={authorId}
        evaluationId={evaluationId}
        orderNo={orderNo}
        name="course"
        isSubmitting={isSubmitting}
        alreadySelectedIds={alreadySelectedIds}
        onFilter={(op, search) => {
          const titleMatch = op.title.toLowerCase().indexOf(search.toLowerCase()) !== -1;
          const authorsMatch =
            op.authors.filter(au => au.userName.toLowerCase().indexOf(search.toLowerCase()) !== -1).length > 0;

          return titleMatch || authorsMatch;
        }}
      />
    );
  }

  if (criteriaType === 'SELECT_BOOK') {
    return (
      <BaseResearchPerfEvalSelectControl<Book>
        label="Carte"
        renderItem={book => <BookCard book={book} />}
        getTitle={book => book.title}
        getId={book => book.id}
        onView={book => {
          window.open(`/author/book/${book.id}`);
        }}
        authorId={authorId}
        evaluationId={evaluationId}
        orderNo={orderNo}
        name="book"
        isSubmitting={isSubmitting}
        alreadySelectedIds={alreadySelectedIds}
        onFilter={(op, search) => {
          const titleMatch = op.title.toLowerCase().indexOf(search.toLowerCase()) !== -1;
          const authorsMatch =
            op.authors.filter(au => au.userName.toLowerCase().indexOf(search.toLowerCase()) !== -1).length > 0;

          return titleMatch || authorsMatch;
        }}
      />
    );
  }

  if (criteriaType === 'SELECT_ARTICLE') {
    return (
      <BaseResearchPerfEvalSelectControl<Article>
        label="Articol"
        renderItem={article => (
          <Stack gap="5px">
            <ArticleCard article={article} />
            <ArticleBestQ articleId={article.id} />
          </Stack>
        )}
        getTitle={article => article.title}
        getId={article => article.id}
        onView={article => {
          window.open(`/author/article/${article.id}`);
        }}
        authorId={authorId}
        evaluationId={evaluationId}
        orderNo={orderNo}
        name="article"
        isSubmitting={isSubmitting}
        alreadySelectedIds={alreadySelectedIds}
        onFilter={(op, search) => {
          const titleMatch = op.title.toLowerCase().indexOf(search.toLowerCase()) !== -1;
          const authorsMatch =
            op.authors.filter(au => au.userName.toLowerCase().indexOf(search.toLowerCase()) !== -1).length > 0;

          return titleMatch || authorsMatch;
        }}
      />
    );
  }

  if (criteriaType === 'SELECT_PROCEEDING') {
    return (
      <BaseResearchPerfEvalSelectControl<Proceeding>
        label="Proceeding"
        renderItem={proceeding => <ProceedingCard proceeding={proceeding} />}
        getTitle={proceeding => proceeding.title}
        getId={proceeding => proceeding.id}
        onView={proceeding => {
          window.open(`/author/proceeding/${proceeding.id}`);
        }}
        authorId={authorId}
        evaluationId={evaluationId}
        orderNo={orderNo}
        name="proceeding"
        isSubmitting={isSubmitting}
        alreadySelectedIds={alreadySelectedIds}
        onFilter={(op, search) => {
          const titleMatch = op.title.toLowerCase().indexOf(search.toLowerCase()) !== -1;
          const authorsMatch =
            op.authors.filter(au => au.userName.toLowerCase().indexOf(search.toLowerCase()) !== -1).length > 0;

          return titleMatch || authorsMatch;
        }}
      />
    );
  }

  if (criteriaType === 'SELECT_TRANSLATION') {
    return (
      <BaseResearchPerfEvalSelectControl<Translation>
        label="Traducere"
        renderItem={translation => <TranslationCard translation={translation} />}
        getTitle={translation => translation.title}
        getId={translation => translation.id}
        onView={translation => {
          window.open(`/author/translation/${translation.id}`);
        }}
        authorId={authorId}
        evaluationId={evaluationId}
        orderNo={orderNo}
        name="translation"
        isSubmitting={isSubmitting}
        alreadySelectedIds={alreadySelectedIds}
        onFilter={(op, search) => {
          const titleMatch = op.title.toLowerCase().indexOf(search.toLowerCase()) !== -1;
          const authorsMatch =
            op.authors.filter(au => au.userName.toLowerCase().indexOf(search.toLowerCase()) !== -1).length > 0;

          return titleMatch || authorsMatch;
        }}
      />
    );
  }

  if (criteriaType === 'SELECT_CHAPTER') {
    return (
      <BaseResearchPerfEvalSelectControl<Chapter>
        label="Capitol"
        renderItem={chapter => <ChapterCard chapter={chapter} />}
        getTitle={chapter => chapter.title}
        getId={chapter => chapter.id}
        onView={chapter => {
          window.open(`/author/chapter/${chapter.id}`);
        }}
        authorId={authorId}
        evaluationId={evaluationId}
        orderNo={orderNo}
        name="chapter"
        isSubmitting={isSubmitting}
        alreadySelectedIds={alreadySelectedIds}
        onFilter={(op, search) => {
          const titleMatch = op.title.toLowerCase().indexOf(search.toLowerCase()) !== -1;
          const authorsMatch =
            op.authors.filter(au => au.userName.toLowerCase().indexOf(search.toLowerCase()) !== -1).length > 0;

          return titleMatch || authorsMatch;
        }}
      />
    );
  }

  if (criteriaType === 'SELECT_COMMITTEE') {
    return (
      <BaseResearchPerfEvalSelectControl<Committee>
        label="Comisie"
        renderItem={committee => <CommitteeCard committee={committee} />}
        getTitle={committee => committee.title}
        getId={committee => committee.id}
        onView={committee => {
          window.open(`/author/committee/${committee.id}`);
        }}
        authorId={authorId}
        evaluationId={evaluationId}
        orderNo={orderNo}
        name="committee"
        isSubmitting={isSubmitting}
        alreadySelectedIds={alreadySelectedIds}
        onFilter={(op, search) => {
          const titleMatch = op.title.toLowerCase().indexOf(search.toLowerCase()) !== -1;
          const authorsMatch =
            op.authors.filter(au => au.userName.toLowerCase().indexOf(search.toLowerCase()) !== -1).length > 0;

          return titleMatch || authorsMatch;
        }}
      />
    );
  }

  if (criteriaType === 'SELECT_PROJECT') {
    return (
      <BaseResearchPerfEvalSelectControl<Project>
        label="Proiect"
        renderItem={project => <ProjectCard project={project} />}
        getTitle={project => project.title}
        getId={project => project.id}
        onView={project => {
          window.open(`/author/project/${project.id}`);
        }}
        authorId={authorId}
        evaluationId={evaluationId}
        orderNo={orderNo}
        name="project"
        isSubmitting={isSubmitting}
        alreadySelectedIds={alreadySelectedIds}
        onFilter={(op, search) => {
          const titleMatch = op.title.toLowerCase().indexOf(search.toLowerCase()) !== -1;
          const authorsMatch =
            op.authors.filter(au => au.userName.toLowerCase().indexOf(search.toLowerCase()) !== -1).length > 0;

          return titleMatch || authorsMatch;
        }}
      />
    );
  }

  if (criteriaType === 'SELECT_PATENT') {
    return (
      <BaseResearchPerfEvalSelectControl<Patent>
        label="Brevet"
        renderItem={patent => <PatentCard patent={patent} />}
        getTitle={patent => patent.title}
        getId={patent => patent.id}
        onView={patent => {
          window.open(`/author/patent/${patent.id}`);
        }}
        authorId={authorId}
        evaluationId={evaluationId}
        orderNo={orderNo}
        name="patent"
        isSubmitting={isSubmitting}
        alreadySelectedIds={alreadySelectedIds}
        onFilter={(op, search) => {
          const titleMatch = op.title.toLowerCase().indexOf(search.toLowerCase()) !== -1;
          const authorsMatch =
            op.authors.filter(au => au.userName.toLowerCase().indexOf(search.toLowerCase()) !== -1).length > 0;

          return titleMatch || authorsMatch;
        }}
      />
    );
  }

  if (criteriaType === 'SELECT_ABSTRACT') {
    return (
      <BaseResearchPerfEvalSelectControl<Abstract>
        label="Rezumat"
        renderItem={abstract => <AbstractCard abstract={abstract} />}
        getTitle={abstract => abstract.title}
        getId={abstract => abstract.id}
        onView={abstract => {
          window.open(`/author/abstract/${abstract.id}`);
        }}
        authorId={authorId}
        evaluationId={evaluationId}
        orderNo={orderNo}
        name="abstractItem"
        isSubmitting={isSubmitting}
        alreadySelectedIds={alreadySelectedIds}
        onFilter={(op, search) => {
          const titleMatch = op.title.toLowerCase().indexOf(search.toLowerCase()) !== -1;
          const authorsMatch =
            op.authors.filter(au => au.userName.toLowerCase().indexOf(search.toLowerCase()) !== -1).length > 0;

          return titleMatch || authorsMatch;
        }}
      />
    );
  }

  if (criteriaType === 'SELECT_AWARD') {
    return (
      <BaseResearchPerfEvalSelectControl<Award>
        label="Premiu"
        renderItem={award => <AwardCard award={award} />}
        getTitle={award => award.title}
        getId={award => award.id}
        onView={award => {
          window.open(`/author/award/${award.id}`);
        }}
        authorId={authorId}
        evaluationId={evaluationId}
        orderNo={orderNo}
        name="award"
        isSubmitting={isSubmitting}
        alreadySelectedIds={alreadySelectedIds}
        onFilter={(op, search) => {
          const titleMatch = op.title.toLowerCase().indexOf(search.toLowerCase()) !== -1;
          const authorsMatch =
            op.authors.filter(au => au.userName.toLowerCase().indexOf(search.toLowerCase()) !== -1).length > 0;

          return titleMatch || authorsMatch;
        }}
      />
    );
  }

  if (criteriaType === 'SELECT_DICTIONARY') {
    return (
      <BaseResearchPerfEvalSelectControl<Dictionary>
        label={t('sidebar.dictionaries')}
        renderItem={dictionary => <DictionaryCard dictionary={dictionary} />}
        getTitle={dictionary => dictionary.title}
        getId={dictionary => dictionary.id}
        onView={dictionary => {
          window.open(`/author/dictionary/${dictionary.id}`);
        }}
        authorId={authorId}
        evaluationId={evaluationId}
        orderNo={orderNo}
        name="dictionary"
        isSubmitting={isSubmitting}
        alreadySelectedIds={alreadySelectedIds}
        onFilter={(op, search) => {
          const titleMatch = op.title.toLowerCase().indexOf(search.toLowerCase()) !== -1;
          const authorsMatch =
            op.authors.filter(au => au.userName.toLowerCase().indexOf(search.toLowerCase()) !== -1).length > 0;

          return titleMatch || authorsMatch;
        }}
      />
    );
  }

  if (criteriaType === 'SELECT_PRESENTATION') {
    return (
      <BaseResearchPerfEvalSelectControl<Presentation>
        label={t('sidebar.presentations')}
        renderItem={presentation => <PresentationCard presentation={presentation} />}
        getTitle={presentation => presentation.title}
        getId={presentation => presentation.id}
        onView={presentation => {
          window.open(`/author/presentation/${presentation.id}`);
        }}
        authorId={authorId}
        evaluationId={evaluationId}
        orderNo={orderNo}
        name="presentation"
        isSubmitting={isSubmitting}
        alreadySelectedIds={alreadySelectedIds}
        onFilter={(op, search) => {
          const titleMatch = op.title.toLowerCase().indexOf(search.toLowerCase()) !== -1;
          const authorsMatch =
            op.authors.filter(au => au.userName.toLowerCase().indexOf(search.toLowerCase()) !== -1).length > 0;

          return titleMatch || authorsMatch;
        }}
      />
    );
  }

  if (criteriaType === 'SELECT_EDITORIAL_TEAM') {
    return (
      <BaseResearchPerfEvalSelectControl<EditorialTeam>
        label={t('sidebar.editorialTeams')}
        renderItem={editorialTeam => <EditorialTeamCard editorialTeam={editorialTeam} />}
        getTitle={editorialTeam => editorialTeam.title}
        getId={editorialTeam => editorialTeam.id}
        onView={editorialTeam => {
          window.open(`/author/editorial-team/${editorialTeam.id}`);
        }}
        authorId={authorId}
        evaluationId={evaluationId}
        orderNo={orderNo}
        name="editorialTeam"
        isSubmitting={isSubmitting}
        alreadySelectedIds={alreadySelectedIds}
        onFilter={(op, search) => {
          const titleMatch =
            (op.journalName ?? op.publisherName ?? '').toLowerCase().indexOf(search.toLowerCase()) !== -1;
          const authorsMatch =
            op.authors.filter(au => au.userName.toLowerCase().indexOf(search.toLowerCase()) !== -1).length > 0;

          return titleMatch || authorsMatch;
        }}
      />
    );
  }

  if (criteriaType === 'SELECT_DOCTORAL_COMMITTEE') {
    return (
      <BaseResearchPerfEvalSelectControl<DoctoralCommittee>
        label={t('sidebar.doctoralCommittees')}
        renderItem={doctoralCommittee => <DoctoralCommitteeCard doctoralCommittee={doctoralCommittee} />}
        getTitle={doctoralCommittee => doctoralCommittee.title}
        getId={doctoralCommittee => doctoralCommittee.id}
        onView={doctoralCommittee => {
          window.open(`/author/doctoralCommittee/${doctoralCommittee.id}`);
        }}
        authorId={authorId}
        evaluationId={evaluationId}
        orderNo={orderNo}
        name="doctoralCommittee"
        isSubmitting={isSubmitting}
        alreadySelectedIds={alreadySelectedIds}
        onFilter={(op, search) => {
          const titleMatch =
            (op.doctoralSchoolName + ' - ' + op.candidateName + ' | ' + op.coordinatorName)
              .toLowerCase()
              .indexOf(search.toLowerCase()) !== -1;
          const authorsMatch =
            op.authors.filter(au => au.userName.toLowerCase().indexOf(search.toLowerCase()) !== -1).length > 0;

          return titleMatch || authorsMatch;
        }}
      />
    );
  }

  assertUnreachable(criteriaType);
}

function getSchema(criteriaType: EvalTemplateCriteriaType) {
  if (criteriaType === 'FREE_ANSWER') {
    return freeCriteriaItemSchema;
  }

  if (criteriaType === 'BOOLEAN') {
    return booleanCriteriaItemSchema;
  }

  if (criteriaType === 'NUMBER') {
    return numberCriteriaItemSchema;
  }

  if (criteriaType === 'SELECT_COURSE') {
    return courseCriteriaItemSchema;
  }

  if (criteriaType === 'SELECT_ABSTRACT') {
    return abstractCriteriaItemSchema;
  }

  if (criteriaType === 'SELECT_ARTICLE') {
    return articleCriteriaItemSchema;
  }

  if (criteriaType === 'SELECT_COMMITTEE') {
    return committeeCriteriaItemSchema;
  }

  if (criteriaType === 'SELECT_PROJECT') {
    return projectCriteriaItemSchema;
  }

  if (criteriaType === 'SELECT_PATENT') {
    return patentCriteriaItemSchema;
  }

  if (criteriaType === 'SELECT_CHAPTER') {
    return chapterCriteriaItemSchema;
  }

  if (criteriaType === 'SELECT_BOOK') {
    return bookCriteriaItemSchema;
  }

  if (criteriaType === 'SELECT_TRANSLATION') {
    return translationCriteriaItemSchema;
  }

  if (criteriaType === 'SELECT_PROCEEDING') {
    return proceedingCriteriaItemSchema;
  }

  if (criteriaType === 'SELECT_AWARD') {
    return awardCriteriaItemSchema;
  }

  if (criteriaType === 'SELECT_DICTIONARY') {
    return dictionaryCriteriaItemSchema;
  }

  if (criteriaType === 'SELECT_PRESENTATION') {
    return presentationCriteriaItemSchema;
  }

  if (criteriaType === 'SELECT_DOCTORAL_COMMITTEE') {
    return doctoralCommitteeCriteriaItemSchema;
  }

  if (criteriaType === 'SELECT_EDITORIAL_TEAM') {
    return editorialTeamCriteriaItemSchema;
  }

  assertUnreachable(criteriaType);
}

export function ResearchPerfEvalCriteriaItemForm<T extends BaseCriteriaItemSchema>(
  props: BaseResearchPerfEvalCriteriaItemFormProps<T>,
) {
  const {
    onSubmit,
    isSubmitting,
    onCancel,
    defaultValues,
    criteriaType,
    dataEntryHint,
    authorId,
    evaluationId,
    orderNo,
    alreadySelectedIds,
  } = props;
  const { t } = useTranslation();
  return (
    <BaseResearchPerfEvalCriteriaItemForm
      onCancel={onCancel}
      schema={getSchema(criteriaType)}
      onChange={onSubmit}
      isSubmitting={isSubmitting}
      defaultValues={defaultValues}
      renderExtraFields={() => (
        <>
          {renderExtraFormFields(
            isSubmitting,
            criteriaType,
            t,
            dataEntryHint,
            authorId,
            evaluationId,
            orderNo,
            alreadySelectedIds,
          )}
          {dataEntryHint && (
            <Text
              fw="500"
              c="dimmed"
              size="sm"
            >
              {dataEntryHint}
            </Text>
          )}
        </>
      )}
    />
  );
}
